import { createContext, useContext, type ReactNode } from "react";

export interface FeatureFlagContextValue {
  /** Set of enabled feature flag keys */
  enabledFlags: Set<string>;
  /** Whether the flags are still loading */
  isLoading: boolean;
  /** Check if a specific flag is enabled */
  isEnabled: (key: string) => boolean;
}

/**
 * Feature-flag context. The PROVIDER (which evaluates flags over tRPC) lives in
 * the consuming app (client/src/core/plugins/feature-flags); this package owns
 * only the context object + pure consumers so plugins can read it without
 * coupling to the app (D-20). Single React instance (peerDep) keeps identity stable.
 */
export const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  enabledFlags: new Set(),
  isLoading: true,
  isEnabled: () => false,
});

/** Hook to access the preloaded feature-flag context. */
export function useFeatureFlags(): FeatureFlagContextValue {
  return useContext(FeatureFlagContext);
}

/**
 * Evaluate a single feature flag. Returns false while flags are loading
 * (safe default — features hidden until confirmed enabled).
 */
export function useFeatureFlag(flagKey: string): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagKey);
}

interface FeatureGateProps {
  flag: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on feature-flag evaluation. Renders
 * `children` when enabled, `fallback` (default null) otherwise.
 */
export function FeatureGate({
  flag,
  children,
  fallback = null,
}: FeatureGateProps) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}
